import {
  DEFAULT_LOCALE,
  DEFAULT_LOCALE_URL_STRATEGY,
  DEFAULT_SITE_URL,
  SEO_X_DEFAULT_LOCALE as SHARED_SEO_X_DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  buildAbsoluteUrl,
  buildSeoUrls,
  normalizeCanonicalPath,
  normalizeLocaleUrlStrategy,
  resolveSiteUrl,
  stripLocalePrefixFromPath,
} from './seo.shared.js';

type Locale = 'tr' | 'en';
type LocaleUrlStrategy = 'none' | 'query' | 'path';
type PageSchemaType = 'WebPage' | 'CollectionPage' | 'AboutPage' | 'ContactPage' | 'Article';

const envSiteUrl = import.meta.env.VITE_SITE_URL ?? import.meta.env.NEXT_PUBLIC_SITE_URL;
const envLocaleStrategy = import.meta.env.VITE_LOCALE_URL_STRATEGY;

export const SITE_URL = resolveSiteUrl(envSiteUrl);
export const SITE_NAME = 'Figtures';
/** Site-wide fallback OG image. Blog/project pages override via SEOHead when cover/hero exists (F38 custom art = design backlog). */
export const SEO_DEFAULT_OG_IMAGE = buildAbsoluteUrl(SITE_URL, '/opengraph.jpeg');
export const SEO_DEFAULT_OG_IMAGE_WIDTH = 1200;
export const SEO_DEFAULT_OG_IMAGE_HEIGHT = 630;
export const SEO_DEFAULT_OG_IMAGE_TYPE = 'image/jpeg';
/**
 * schema.org Organization logo. Must resolve to a real crawlable file — a 404 here means
 * Google drops the logo (and with it the brand-panel image) without reporting an error.
 * Keep in sync with scripts/generate-entity-jsonld.mjs.
 */
export const SEO_BRAND_LOGO = buildAbsoluteUrl(SITE_URL, '/ico/android-chrome-512x512.png');
export const SEO_BRAND_LOGO_SIZE = 512;
export const SEO_SUPPORTED_LOCALES = SUPPORTED_LOCALES as Locale[];
export const SEO_DEFAULT_LOCALE = DEFAULT_LOCALE as Locale;
export const SEO_X_DEFAULT_LOCALE = SHARED_SEO_X_DEFAULT_LOCALE as Locale;

export const SEO_LOCALE_URL_STRATEGY =
  normalizeLocaleUrlStrategy(envLocaleStrategy) as LocaleUrlStrategy;

const ROUTE_METADATA_DEFAULTS: Record<
  string,
  { tr: { title: string; description: string }; en: { title: string; description: string } }
> = {
  '/': {
    tr: {
      title: 'Figtures Product Studio | Yapay Zeka, SaaS ve Özel Yazılım – İstanbul',
      description:
        'Fikirden canlı ürüne: AI geliştirme, SaaS/MVP, özel yazılım ve mobil uygulama. İstanbul merkezli product studio; discovery’dan production’a tek ekip.',
    },
    en: {
      title: 'Figtures | AI, SaaS & Custom Software Development',
      description:
        'From idea to live product: AI development, SaaS/MVP, custom software, and mobile apps. Istanbul-based product studio from discovery through production.',
    },
  },
  '/about': {
    tr: {
      title: 'Figtures Hakkında | İstanbul Yazılım ve AI Product Studio',
      description:
        'Figtures, Ataşehir İstanbul merkezli product studio. AI, özel yazılım, SaaS/MVP, web ve mobil projeleri keşiften canlıya tek ekiple yürütür. Süreç, ekip ve şirket bilgileri.',
    },
    en: {
      title: 'About Figtures | Istanbul Software and AI Product Studio',
      description:
        'Figtures is a product studio in Ataşehir, Istanbul, delivering AI, custom software, SaaS/MVP, web and mobile products end to end. Process, team and company facts.',
    },
  },
  '/linkedin': {
    tr: {
      title: 'Figtures LinkedIn — Resmi Şirket Sayfası',
      description:
        'Figtures’ın resmi LinkedIn şirket profili, product studio tanımı ve figtures.com bağlantısı. Ücretsiz LinkedIn About aracından farklıdır.',
    },
    en: {
      title: 'Figtures on LinkedIn — Official Company Page',
      description:
        'Figtures’ official LinkedIn company profile and product studio positioning on figtures.com — distinct from the free LinkedIn About generator.',
    },
  },
  '/ai-answers': {
    tr: {
      title: 'Figtures AI Cevapları | GEO/AEO Canonical Kaynak',
      description:
        'Figtures hakkında AI arama motorları için doğrudan, kaynak gösterilebilir cevaplar ve 2026 benchmark sinyalleri.',
    },
    en: {
      title: 'Figtures AI Answers | GEO/AEO Canonical Source',
      description:
        'Direct, citable answers about Figtures for AI search engines—plus 2026 benchmark signals.',
    },
  },
  '/contact': {
    tr: {
      title: 'İletişim | Figtures',
      description:
        'AI, SaaS ve özel yazılım projeleri için İstanbul merkezli Figtures ürün ve mühendislik ekibine yazın.',
    },
    en: {
      title: 'Contact | Figtures',
      description:
        'Contact the Istanbul-based Figtures product and engineering team for AI, SaaS, and custom software work.',
    },
  },
  '/cookie-policy': {
    tr: {
      title: 'Çerez Politikası',
      description:
        'Figtures çerez politikası: zorunlu ve isteğe bağlı çerezler, rıza yönetimi ve envanter güncellemeleri.',
    },
    en: {
      title: 'Cookie Policy',
      description:
        'Figtures cookie policy: essential and optional cookies, consent management, and inventory updates.',
    },
  },
  '/kvkk-aydinlatma-metni': {
    tr: {
      title: 'KVKK Aydınlatma Metni',
      description:
        'Figtures KVKK aydınlatma metni: veri sorumlusu, işleme amaçları, aktarım, saklama ve ilgili kişi hakları.',
    },
    en: {
      title: 'KVKK Clarification Text',
      description:
        'Figtures KVKK clarification: controller, purposes, transfers, retention, and data subject rights.',
    },
  },
  '/delivery-cancellation-refund': {
    tr: {
      title: 'Teslim, İptal ve İade Koşulları',
      description:
        'Figtures teslim, iptal ve iade koşulları: dijital teslimat, tüketici cayma hakkı ve B2B fesih esasları.',
    },
    en: {
      title: 'Delivery, Cancellation & Refunds',
      description:
        'Figtures delivery, cancellation and refund terms for project-based digital services.',
    },
  },
  '/distance-sales-agreement': {
    tr: {
      title: 'Mesafeli Satış Sözleşmesi',
      description:
        'Figtures mesafeli satış sözleşmesi ve ön bilgilendirme formu: taraflar, hizmet, bedel, iyzico ile ödeme, dijital teslim ve cayma hakkı.',
    },
    en: {
      title: 'Distance Sales Agreement',
      description:
        'Figtures distance sales agreement and pre-contract information for services purchased online.',
    },
  },
};

function normalizePathnameOnly(canonicalPath: string) {
  return stripLocalePrefixFromPath(normalizeCanonicalPath(canonicalPath));
}

function humanizeSlug(slug: string) {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ''}${part.slice(1)}`)
    .join(' ');
}

export function getRouteSeoDefaults(canonicalPath: string, locale: Locale) {
  const normalizedPath = normalizePathnameOnly(canonicalPath);
  const routeDefaults = ROUTE_METADATA_DEFAULTS[normalizedPath];
  if (!routeDefaults) return null;
  return routeDefaults[locale];
}

export function inferPageSchemaType(canonicalPath: string, contentType: 'website' | 'article'): PageSchemaType {
  if (contentType === 'article') return 'Article';
  const normalizedPath = normalizePathnameOnly(canonicalPath);
  if (normalizedPath === '/about') return 'AboutPage';
  if (normalizedPath === '/contact') return 'ContactPage';
  if (
    normalizedPath === '/services' ||
    normalizedPath === '/solutions' ||
    normalizedPath === '/projects' ||
    normalizedPath === '/blog' ||
    normalizedPath === '/tools' ||
    normalizedPath === '/playbooks' ||
    normalizedPath === '/ai-answers'
  ) {
    return 'CollectionPage';
  }
  if (normalizedPath.startsWith('/blog/tag/')) return 'CollectionPage';
  return 'WebPage';
}

export function buildDerivedSeoDefaults(canonicalPath: string, locale: Locale) {
  const pathname = normalizePathnameOnly(canonicalPath);
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 2 && segments[0] === 'services') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} Hizmeti`,
          description:
            `Figtures ${label} hizmeti: AI ve yazılım ürün geliştirme süreçleri için global product engineering teslimat yaklaşımı.`,
        }
      : {
          title: `${label} Service`,
          description:
            `Figtures ${label} service for global AI and software product engineering delivery.`,
        };
  }

  if (segments.length === 2 && segments[0] === 'solutions') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} Çözümü`,
          description:
            `Figtures ${label} çözümü: AI entegrasyonları, workflow kurgusu ve product engineering delivery kapsamı.`,
        }
      : {
          title: `${label} Solution`,
          description:
            `Figtures ${label} solution for AI integrations, workflow architecture, and product engineering delivery.`,
        };
  }

  if (segments.length === 2 && segments[0] === 'playbooks') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} Playbook`,
          description:
            `Figtures playbook içeriği: AI ürün geliştirme, workflow seçimi ve uygulama kararları için teknik rehber.`,
        }
      : {
          title: `${label} Playbook`,
          description:
            `Figtures playbook content for AI product development, workflow selection, and implementation decisions.`,
        };
  }

  if (segments.length === 2 && segments[0] === 'tools') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} Aracı`,
          description:
            `Figtures ${label} aracı: ürün ve büyüme ekipleri için ücretsiz yardımcı üretkenlik aracı.`,
        }
      : {
          title: `${label} Tool`,
          description:
            `Figtures ${label} tool for product and growth teams.`,
        };
  }

  if (segments.length === 2 && segments[0] === 'blog') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} | Figtures Blog`,
          description:
            'Figtures blog içeriği: AI, yazılım mimarisi, ürün geliştirme ve engineering delivery pratikleri.',
        }
      : {
          title: `${label} | Figtures Blog`,
          description:
            'Figtures blog content on AI, software architecture, product development, and engineering delivery.',
        };
  }

  if (segments.length === 2 && segments[0] === 'projects') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} Projesi`,
          description:
            'Figtures proje örneği: AI ve yazılım ürün geliştirme teslimat yaklaşımı ve çıktılar.',
        }
      : {
          title: `${label} Project`,
          description:
            'Figtures project case covering AI and software product engineering delivery.',
        };
  }

  if (segments.length === 2 && segments[0] === 'ads') {
    const label = humanizeSlug(segments[1]);
    return locale === 'tr'
      ? {
          title: `${label} Çözümü`,
          description:
            'Figtures tarafından sunulan AI ve yazılım odaklı çözüm yaklaşımı hakkında bilgi alın.',
        }
      : {
          title: `${label} Solution`,
          description:
            'Learn about Figtures AI and software-oriented solution approach.',
        };
  }

  if (pathname === '/services') {
    return locale === 'tr'
      ? {
          title: 'Özel Yazılım ve AI Geliştirme Hizmetleri',
          description:
            'AI geliştirme, özel yazılım, SaaS/MVP, mobil uygulama ve UI/UX. Discovery’dan production’a tek ekip çalışır. Projenizi anlatın, kapsamı birlikte netleştirelim.',
        }
      : {
          title: 'Custom Software & AI Development Services',
          description:
            'AI development, custom software, SaaS/MVP, mobile apps, and UI/UX. One team from discovery through production. Tell us your project and we’ll scope it together.',
        };
  }

  if (pathname === '/solutions') {
    return locale === 'tr'
      ? {
          title: 'AI, SaaS ve Mobil Çözümler',
          description:
            'AI entegrasyonu, custom software, SaaS/MVP ve mobil uygulama çözümleri. Hangi kapsamın size uyduğunu görün, ekibimizle konuşarak başlayın.',
        }
      : {
          title: 'AI, SaaS & Mobile Solutions',
          description:
            'AI integration, custom software, SaaS/MVP, and mobile app solutions. See which scope fits your goals and start the conversation with our team.',
        };
  }

  if (pathname === '/tools') {
    return locale === 'tr'
      ? {
          title: 'Ücretsiz AI Araçları — Üyelik Gerektirmez',
          description:
            'Ücretsiz AI araçları: ATS CV analizi, iş ilanı yazıcı, fiyat teklifi, motivasyon mektubu, Meta reklam metni ve daha fazlası. Kayıt olmadan tarayıcıdan kullanın.',
        }
      : {
          title: 'Free AI Tools — No Sign-Up Required',
          description:
            'Free AI tools: ATS CV scoring, job posting writer, price quotes, cover letters, Meta ad copy, and more. Run them straight from your browser—no account needed.',
        };
  }

  if (pathname === '/blog') {
    return locale === 'tr'
      ? {
          title: 'AI, SaaS ve Yazılım Geliştirme Blogu',
          description:
            'AI, SaaS, ürün tasarımı ve yazılım mühendisliği üzerine uygulanabilir rehberler, teknik içgörüler ve saha deneyimlerinden çıkan dersler.',
        }
      : {
          title: 'AI, SaaS & Software Engineering Blog',
          description:
            'Actionable guides, technical insights, and field lessons on AI, SaaS, product design, and software engineering.',
        };
  }

  if (pathname === '/projects') {
    return locale === 'tr'
      ? {
          title: 'Yazılım Projeleri ve Case Study\'ler',
          description:
            'AI, SaaS, kurumsal web ve ürün arayüzü projelerimiz: kullanılan teknolojiler, çözülen problemler ve teslim edilen sonuçlar. Türkiye ve global müşteriler.',
        }
      : {
          title: 'Software Projects & Case Studies',
          description:
            'Our AI, SaaS, corporate web, and product UI work: the technologies used, problems solved, and outcomes delivered for clients in Turkey and abroad.',
        };
  }

  if (pathname === '/privacy') {
    return locale === 'tr'
      ? {
          title: 'Gizlilik Politikası',
          description:
            'Figtures gizlilik politikası: kişisel verilerin toplanması, işlenmesi, saklanması ve haklarınız.',
        }
      : {
          title: 'Privacy Policy',
          description:
            'Figtures privacy policy covering personal data collection, processing, storage, and your rights.',
        };
  }

  if (pathname === '/kvkk-aydinlatma-metni') {
    return locale === 'tr'
      ? {
          title: 'KVKK Aydınlatma Metni',
          description:
            'Figtures KVKK Aydınlatma Metni: işlenen kişisel veri kategorileri, işleme amaçları, aktarım çerçevesi ve KVKK m.11 hakları.',
        }
      : {
          title: 'KVKK Clarification Text (Informational)',
          description:
            'Informational English companion for Figtures KVKK Clarification Text. Turkish text is authoritative.',
        };
  }

  if (pathname === '/terms') {
    return locale === 'tr'
      ? {
          title: 'Kullanım Koşulları',
          description:
            'Figtures kullanım koşulları: hizmet kapsamı, fikri mülkiyet, ödeme ve sorumluluk hükümleri.',
        }
      : {
          title: 'Terms of Service',
          description:
            'Figtures terms of service covering scope, intellectual property, payment, and liability.',
        };
  }

  return null;
}

/** Meta description / OG text — keep under ~158 chars for SERP snippets */
const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  avif: 'image/avif',
  gif: 'image/gif',
};

export function inferImageMimeType(url: string): string | undefined {
  const extension = url.split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase();
  return extension ? IMAGE_MIME_BY_EXTENSION[extension] : undefined;
}

export function truncateMeta(text: string, max = 158): string {
  const normalized = text.trim().replace(/\s+/g, ' ');
  if (normalized.length <= max) return normalized;
  const cut = normalized.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const base = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd();
  return `${base}…`;
}

export function getSeoUrls({
  canonicalPath,
  locale,
}: {
  canonicalPath: string;
  locale: Locale;
}) {
  return buildSeoUrls({
    siteUrl: SITE_URL,
    canonicalPath,
    locale,
    supportedLocales: SEO_SUPPORTED_LOCALES,
    defaultLocale: SEO_DEFAULT_LOCALE,
    xDefaultLocale: SEO_X_DEFAULT_LOCALE,
    localeUrlStrategy: SEO_LOCALE_URL_STRATEGY,
  });
}

export {
  DEFAULT_SITE_URL,
  DEFAULT_LOCALE_URL_STRATEGY,
  buildAbsoluteUrl,
  normalizeCanonicalPath,
  normalizeLocaleUrlStrategy,
  resolveSiteUrl,
  stripLocalePrefixFromPath,
};
